import {parseAmountToCents} from "../public/tip.util";
import {TipBase, TipFormat, TipUnit} from "../public/digitalSigning.types";
import {
    DEFAULT_DESIGN,
    DigitalSigningDesign,
    DigitalSigningTemplate,
    DigitalSigningTemplateSaveRequest,
    DigitalSigningTipSettings,
    PAGE_LANGUAGES,
    PageLanguage,
    SUBTITLE_MAX_LENGTH,
    TEMPLATE_NAME_MAX_LENGTH,
    THANK_YOU_MAX_LENGTH,
    TIP_DESCRIPTION_MAX_LENGTH,
    TIP_LABEL_MAX_LENGTH,
    TIP_SUGGESTIONS,
    TITLE_MAX_LENGTH
} from "./digitalSigningTemplate.model";

//region The form: the template as the page holds it, numbers as typed

export type PageTexts = Record<PageLanguage, string>;

export interface TipSettingsForm {
    enabled: boolean
    format: TipFormat
    smartLowerUnit: TipUnit
    smartUpperUnit: TipUnit
    amounts: string[]
    /** Smart's amounts from the threshold. */
    upperAmounts: string[]
    percents: string[]
    /** Smart's percentages below the threshold. */
    lowerPercents: string[]
    threshold: string
    base: TipBase
    roundingCents: number
    highlightedPresetIndex: number | null
    preselectedPresetIndex: number | null
    labels: PageTexts
    descriptions: PageTexts
}

export interface DigitalSigningTemplateForm {
    name: string
    scopedSectorIds: string[]
    tip: TipSettingsForm
    /** With the font's source: a picked Google Fonts or own font source without its font can't be saved. */
    design: DigitalSigningDesign
    privacyPolicyUrl: string
    imprintUrl: string
    thankYouMessages: PageTexts
    titles: PageTexts
    subtitles: PageTexts
}

const decimalMark = (language: string) => (language.toLowerCase().startsWith('de') ? ',' : '.');

/** Cents as an input's text: "5", "12,50" - whole amounts without decimals. */
export const centsToInput = (cents: number, language: string): string => (
    cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2).replace('.', decimalMark(language))
);

/** A percentage as an input's text: "8", "7,5". */
export const percentToInput = (percent: number, language: string): string => (
    String(Number(percent.toFixed(2))).replace('.', decimalMark(language))
);

// Up to three whole digits, then `,` or `.` and at most two decimals - the input's six characters
const PERCENT = /^(\d{1,3})(?:[.,](\d{1,2}))?$/;

/** "7,5" → 7.5; null for anything that isn't a plain percentage. */
export const parsePercent = (input: string): number | null => {
    const match = PERCENT.exec(input.trim());
    return match ? Number(`${match[1]}.${match[2] ?? '0'}`) : null;
};

/** The saved design with the defaults filled in. */
export const designOf = (template: Pick<DigitalSigningTemplate, 'design'>): DigitalSigningDesign => (
    {...DEFAULT_DESIGN, ...template.design}
);

/** Normalizes localized texts by language, defaulting missing entries to empty strings. */
export const pageTexts = (texts?: Record<string, string>): PageTexts => (
    Object.fromEntries(PAGE_LANGUAGES.map(language => [language, texts?.[language] ?? ''])) as PageTexts
);

/**
 * The tip settings with Smart's units and all four lists. Without them - an older response - amounts below
 * the threshold and percentages from it, with copies of the amounts and the percentages as the other two
 * lists, as the backend reads a template saved before them.
 */
export const completeTipSettings = (tip: DigitalSigningTipSettings): DigitalSigningTemplateSaveRequest['tipSettings'] => ({
    ...tip,
    smartLowerUnit: tip.smartLowerUnit ?? 'FIXED',
    smartUpperUnit: tip.smartUpperUnit ?? 'PERCENT',
    upperFixedPresetsCents: tip.upperFixedPresetsCents ?? tip.fixedPresetsCents,
    lowerPercentPresets: tip.lowerPercentPresets ?? tip.percentPresets,
});

export const formFromTemplate = (template: DigitalSigningTemplate, language: string): DigitalSigningTemplateForm => {
    const tip = completeTipSettings(template.tipSettings);
    const three = <T, >(values: T[], fallback: T) => Array.from({length: TIP_SUGGESTIONS}, (_, index) => values[index] ?? fallback);
    const amountInputs = (cents: number[]) => three(cents, 0).map(value => (value > 0 ? centsToInput(value, language) : ''));
    const percentInputs = (percents: number[]) => three(percents, 0).map(percent => (percent > 0 ? percentToInput(percent, language) : ''));

    return {
        name: template.name ?? '',
        scopedSectorIds: template.scopedSectorIds ?? [],
        design: designOf(template),
        privacyPolicyUrl: template.privacyPolicyUrl ?? '',
        imprintUrl: template.imprintUrl ?? '',
        thankYouMessages: pageTexts(template.thankYouMessages),
        titles: pageTexts(template.titles),
        subtitles: pageTexts(template.subtitles),
        tip: {
            enabled: tip.enabled,
            format: tip.format,
            smartLowerUnit: tip.smartLowerUnit,
            smartUpperUnit: tip.smartUpperUnit,
            amounts: amountInputs(tip.fixedPresetsCents),
            upperAmounts: amountInputs(tip.upperFixedPresetsCents),
            percents: percentInputs(tip.percentPresets),
            lowerPercents: percentInputs(tip.lowerPercentPresets),
            threshold: tip.smartThresholdCents > 0 ? centsToInput(tip.smartThresholdCents, language) : '',
            base: tip.base,
            roundingCents: tip.roundingCents,
            highlightedPresetIndex: tip.highlightedPresetIndex ?? null,
            preselectedPresetIndex: template.preselectAllowed ? tip.preselectedPresetIndex ?? null : null,
            labels: pageTexts(tip.labels),
            descriptions: pageTexts(tip.descriptions),
        },
    };
};

export type TipSuggestionList = 'amounts' | 'upperAmounts' | 'lowerPercents' | 'percents';

/** The suggestion lists the format shows, in column order: one for FIXED and PERCENT; for SMART the list of each tier, below the threshold first. */
export const tipSuggestionLists = (tip: Pick<TipSettingsForm, 'format' | 'smartLowerUnit' | 'smartUpperUnit'>): TipSuggestionList[] => {
    if (tip.format === 'FIXED') return ['amounts'];
    if (tip.format === 'PERCENT') return ['percents'];
    return [
        tip.smartLowerUnit === 'PERCENT' ? 'lowerPercents' : 'amounts',
        tip.smartUpperUnit === 'FIXED' ? 'upperAmounts' : 'percents',
    ];
};

export const isPercentList = (list: TipSuggestionList): boolean => list === 'percents' || list === 'lowerPercents';

export type TemplateFormErrors = {
    name?: string
    amounts: (string | undefined)[]
    upperAmounts: (string | undefined)[]
    percents: (string | undefined)[]
    lowerPercents: (string | undefined)[]
    threshold?: string
    primaryColor?: string
    panelColor?: string
    fontUrl?: string
    privacyPolicyUrl?: string
    imprintUrl?: string
    /** By page language: a message longer than the backend keeps. */
    thankYouMessages: Partial<PageTexts>
    /** By page language: a title or a subtitle longer than the backend keeps. */
    titles: Partial<PageTexts>
    subtitles: Partial<PageTexts>
    /** By page language: a tip name or description longer than the backend keeps. */
    tipLabels: Partial<PageTexts>
    tipDescriptions: Partial<PageTexts>
}

// The backend stores design colors as #RRGGBB
const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

// The backend's rules for the font's link and the footer's links: an https:// address with a host
const HTTPS_URL = /^https:\/\/[^\s"'<>{}\\/]+(\/[^\s"'<>{}\\]*)?$/;

const httpsUrlProblem = (value: string | null | undefined, messages: {tooLong: string, invalid: string}): string | undefined => {
    const url = value?.trim();
    if (!url) return undefined;
    if (url.length > 500) return messages.tooLong;
    return HTTPS_URL.test(url) ? undefined : messages.invalid;
};

/** [message] for each page language whose text is longer than [max] once trimmed, like the backend counts. */
const tooLong = (texts: Partial<PageTexts> | undefined, max: number, message: string): Partial<PageTexts> => (
    Object.fromEntries(PAGE_LANGUAGES
        .filter(language => (texts?.[language]?.trim().length ?? 0) > max)
        .map(language => [language, message]))
);

const amountProblem = (input: string): string | undefined => {
    const cents = parseAmountToCents(input);
    if (cents === null) return 'Please enter a valid amount.';
    return cents > 0 ? undefined : 'Every tip amount must be above 0';
};

// Like the guest's own amount, uncapped: only the input's length limits it
const percentProblem = (input: string): string | undefined => {
    const percent = parsePercent(input);
    if (percent === null) return 'Please enter a valid percentage.';
    return percent > 0 ? undefined : 'Every tip percentage must be above 0';
};

/** The backend's save rules, as messages beside the fields the format shows. */
export const validateTemplateForm = (form: DigitalSigningTemplateForm): TemplateFormErrors => {
    const {tip, design} = form;
    const errors: TemplateFormErrors = {
        amounts: [], upperAmounts: [], percents: [], lowerPercents: [],
        thankYouMessages: {}, titles: {}, subtitles: {}, tipLabels: {}, tipDescriptions: {},
    };
    const name = form.name.trim();
    errors.name = !name
        ? 'A template needs a name'
        : name.length > TEMPLATE_NAME_MAX_LENGTH ? 'A template name has at most 80 characters' : undefined;
    const colorProblem = (value: string | null | undefined, message: string) => (
        value?.trim() && !HEX_COLOR.test(value.trim()) ? message : undefined
    );
    errors.primaryColor = colorProblem(design.primaryColor, 'The primary color must be a hex color like #2E5E4E');
    errors.panelColor = colorProblem(design.panelColor, 'The panel color must be a hex color like #2E5E4E');
    // A Google font or an own font needs both its link and its name, like the backend
    const noFont = !design.fontUrl?.trim() || !design.fontFamily?.trim();
    errors.fontUrl = design.fontSource === 'GOOGLE' && noFont ? 'Choose a font first.'
        : design.fontSource === 'OWN' && noFont ? 'Upload a font file first.'
            : httpsUrlProblem(design.fontUrl, {
                tooLong: 'The font link has at most 500 characters',
                invalid: 'The font link must start with https://',
            });
    errors.privacyPolicyUrl = httpsUrlProblem(form.privacyPolicyUrl, {
        tooLong: 'The privacy policy URL has at most 500 characters',
        invalid: 'The privacy policy URL must be an https:// address',
    });
    errors.imprintUrl = httpsUrlProblem(form.imprintUrl, {
        tooLong: 'The imprint URL has at most 500 characters',
        invalid: 'The imprint URL must be an https:// address',
    });
    errors.thankYouMessages = tooLong(form.thankYouMessages, THANK_YOU_MAX_LENGTH, 'A thank-you message has at most 200 characters');
    errors.titles = tooLong(form.titles, TITLE_MAX_LENGTH, 'A title has at most 60 characters');
    errors.subtitles = tooLong(form.subtitles, SUBTITLE_MAX_LENGTH, 'A subtitle has at most 120 characters');
    if (!tip.enabled) return errors;

    errors.tipLabels = tooLong(tip.labels, TIP_LABEL_MAX_LENGTH, 'A tip name has at most 40 characters');
    errors.tipDescriptions = tooLong(tip.descriptions, TIP_DESCRIPTION_MAX_LENGTH, 'A tip description has at most 200 characters');

    for (const list of tipSuggestionLists(tip)) {
        errors[list] = tip[list].map(isPercentList(list) ? percentProblem : amountProblem);
    }
    if (tip.format === 'SMART') {
        const cents = parseAmountToCents(tip.threshold);
        errors.threshold = cents === null
            ? 'Please enter a valid amount.'
            : cents > 0 ? undefined : 'Smart tips need a threshold above 0';
    }
    return errors;
};

/** The name, a color, the font or a legal link doesn't pass: the page's "Basic info" tab says so. */
export const hasBasicInfoErrors = (errors: TemplateFormErrors): boolean => (
    !!errors.name || !!errors.primaryColor || !!errors.panelColor || !!errors.fontUrl
    || !!errors.privacyPolicyUrl || !!errors.imprintUrl
);

/** The threshold or a suggestion doesn't pass: the "Tip" tab says so. */
export const hasTipErrors = (errors: TemplateFormErrors): boolean => (
    !!errors.threshold
    || [errors.amounts, errors.upperAmounts, errors.percents, errors.lowerPercents].some(list => list.some(Boolean))
);

/** A title, a subtitle, a thank-you or the tip's name or description is too long: the "Texts" tab says so. */
export const hasTextsErrors = (errors: TemplateFormErrors): boolean => (
    [errors.titles, errors.subtitles, errors.thankYouMessages, errors.tipLabels, errors.tipDescriptions]
        .some(texts => Object.values(texts).some(Boolean))
);

export const hasTemplateFormErrors = (errors: TemplateFormErrors): boolean => (
    hasBasicInfoErrors(errors) || hasTipErrors(errors) || hasTextsErrors(errors)
);

/** Converts template form state into save request DTO, preserving unedited backend values. */
export const toSaveRequest = (
    form: DigitalSigningTemplateForm,
    saved: DigitalSigningTemplate
): DigitalSigningTemplateSaveRequest => {
    const {tip} = form;
    const savedTip = completeTipSettings(saved.tipSettings);
    const valid = (value: number | null, ok: (value: number) => boolean, fallback: number) => (
        value !== null && ok(value) ? value : fallback
    );
    // Every list is sent, the ones the format hides too: what doesn't parse keeps what is saved
    const amounts = (inputs: string[], savedCents: number[]) => inputs.map((input, index) => (
        valid(parseAmountToCents(input), cents => cents > 0, savedCents[index] ?? 0)
    ));
    const percents = (inputs: string[], savedPercents: number[]) => inputs.map((input, index) => (
        valid(parsePercent(input), percent => percent > 0, savedPercents[index] ?? 0)
    ));

    const color = (value?: string | null) => value?.trim() || null;
    const text = (value?: string | null) => value?.trim() ?? '';
    // A map replaces the stored one: only the non-blank texts, trimmed - a language left blank shows ours again
    const texts = (values?: Partial<PageTexts>) => Object.fromEntries(PAGE_LANGUAGES
        .map(language => [language, text(values?.[language])])
        .filter(([, value]) => !!value));

    return {
        id: saved.id,
        name: form.name.trim(),
        scopedSectorIds: form.scopedSectorIds,
        design: {
            variant: form.design.variant,
            primaryColor: color(form.design.primaryColor),
            panelColor: color(form.design.panelColor),
            logoMedia: form.design.logoMedia ?? null,
            heroMedia: form.design.heroMedia ?? null,
            // Never set: left out, the backend keeps none - Medium
            heroOpacity: form.design.heroOpacity ?? undefined,
            cornerStyle: form.design.cornerStyle,
            fontSource: form.design.fontSource,
            // A blank string clears the font; null would keep the stored one
            fontUrl: text(form.design.fontUrl),
            fontFamily: text(form.design.fontFamily),
        },
        // Like the font: a blank string clears the link
        privacyPolicyUrl: text(form.privacyPolicyUrl),
        imprintUrl: text(form.imprintUrl),
        thankYouMessages: texts(form.thankYouMessages),
        titles: texts(form.titles),
        subtitles: texts(form.subtitles),
        tipSettings: {
            enabled: tip.enabled,
            format: tip.format,
            smartLowerUnit: tip.smartLowerUnit,
            smartUpperUnit: tip.smartUpperUnit,
            fixedPresetsCents: amounts(tip.amounts, savedTip.fixedPresetsCents),
            upperFixedPresetsCents: amounts(tip.upperAmounts, savedTip.upperFixedPresetsCents),
            percentPresets: percents(tip.percents, savedTip.percentPresets),
            lowerPercentPresets: percents(tip.lowerPercents, savedTip.lowerPercentPresets),
            smartThresholdCents: valid(parseAmountToCents(tip.threshold), cents => cents > 0, savedTip.smartThresholdCents),
            base: tip.base,
            roundingCents: tip.roundingCents,
            highlightedPresetIndex: tip.highlightedPresetIndex,
            preselectedPresetIndex: saved.preselectAllowed ? tip.preselectedPresetIndex : null,
            // Always both maps, so a cleared name or description is cleared on the backend too
            labels: texts(tip.labels),
            descriptions: texts(tip.descriptions),
        },
    };
};

//endregion

//region The rounding of percentage suggestions, as the backend's snapshot rounds them

/** Calculates tip in cents for a given percentage and rounding step. */
export const percentTipCents = (baseCents: number, percent: number, roundingCents: number): number => {
    const exact = baseCents * percent / 100;
    if (roundingCents <= 1) return Math.round(exact);
    if (exact <= 0) return 0;
    return Math.max(roundingCents, Math.round(exact / roundingCents) * roundingCents);
};

export interface TipRoundingExample {
    percent: number
    baseCents: number
    exactCents: number
    roundingCents: number
    amountCents: number
}

const EXAMPLE_BASE_CENTS = 12345;
const EXAMPLE_THRESHOLD_DISTANCE_CENTS = 2345;
const EXAMPLE_PERCENT = 10;

// A bill of 123.45; with Smart 23.45 above the threshold, or below it for the percentages below it -
// half of a threshold too small for that
const exampleBaseCents = (thresholdCents: number | null, below: boolean): number => {
    if (!thresholdCents || thresholdCents <= 0) return EXAMPLE_BASE_CENTS;
    if (!below) return thresholdCents + EXAMPLE_THRESHOLD_DISTANCE_CENTS;
    return thresholdCents > EXAMPLE_THRESHOLD_DISTANCE_CENTS ? thresholdCents - EXAMPLE_THRESHOLD_DISTANCE_CENTS : Math.floor(thresholdCents / 2);
};

/** Computes tip rounding example for display in settings: Smart's percentages from the threshold, else those below it. */
export const tipRoundingExample = (
    tip: Pick<TipSettingsForm, 'format' | 'smartUpperUnit' | 'percents' | 'lowerPercents' | 'threshold' | 'roundingCents'>
): TipRoundingExample => {
    const smart = tip.format === 'SMART';
    const below = smart && tip.smartUpperUnit === 'FIXED';
    const percent = (below ? tip.lowerPercents : tip.percents).map(parsePercent)
        .find((value): value is number => value !== null && value > 0) ?? EXAMPLE_PERCENT;
    const baseCents = exampleBaseCents(smart ? parseAmountToCents(tip.threshold) : null, below);

    return {
        percent,
        baseCents,
        exactCents: percentTipCents(baseCents, percent, 1),
        roundingCents: tip.roundingCents,
        amountCents: percentTipCents(baseCents, percent, tip.roundingCents),
    };
};

//endregion
